import Product from 'App/resources/Model/Product';
import ProductCategory from 'App/resources/Model/ProductCategory';

export type ProductCategoryNode = {
    id: string;
    title: string;
    showChildren: boolean;
    children: ProductTreeNode[];
};

export type ProductLeafNode = Product & { title: string };

export type ProductTreeNode = ProductLeafNode | ProductCategoryNode;

type BuildOpts = {
    onlyRecurring?: boolean;
    searchString?: string;
    currentValueId?: number | string | null;
};

const isCategoryNode = (node: ProductTreeNode): node is ProductCategoryNode =>
    typeof node.id === 'string' && (node.id as string).startsWith('cat-');

export function buildProductTree(
    products: Product[],
    categories: ProductCategory[],
    opts: BuildOpts = {}
): ProductTreeNode[] {
    const { onlyRecurring, searchString, currentValueId } = opts;
    const currentId = currentValueId == null ? null : currentValueId.toString();
    const search = (searchString ?? '').toLowerCase();

    // Replicate the endpoint's `q`: active=true, optional isRecurring, optional name wildcard.
    let filtered = products.filter(p => !!p.active);
    if (onlyRecurring) {
        filtered = filtered.filter(p => !!p.isRecurring);
    }
    if (search.length) {
        filtered = filtered.filter(p => p.name.toLowerCase().includes(search));
    }

    let finalPath: ProductCategory[] = [];

    const buildTree = (
        cats: ProductCategory[],
        prods: Product[],
        parentId?: number,
        path: ProductCategory[] = []
    ): ProductTreeNode[] => {
        const output: ProductTreeNode[] = [];

        for (const category of cats) {
            if (!category.parentId) {
                const subtree = buildTree(
                    cats.filter(c => !!c.parentId),
                    prods.filter(p => !!p.category?.id),
                    category.id,
                    [category, ...path]
                );
                if (subtree.length || !search.length) {
                    output.push({ id: 'cat-' + category.id, title: category.name, showChildren: false, children: subtree });
                }
            } else if (category.parentId === parentId) {
                const subtree = buildTree(
                    cats.filter(c => c.parentId !== parentId),
                    prods.filter(p => p.category?.id !== parentId),
                    category.id,
                    [category, ...path]
                );
                if (subtree.length || !search.length) {
                    output.push({ id: 'cat-' + category.id, title: category.name, showChildren: false, children: subtree });
                }
            }
        }

        for (const product of prods) {
            if (product.category?.id === parentId) {
                if (currentId !== null && product.id.toString() === currentId) {
                    finalPath = path;
                }
                output.push({ ...product, title: product.name });
            }
        }

        return output;
    };

    const tree = buildTree(categories, filtered);

    if (currentId !== null && finalPath.length) {
        let level: ProductTreeNode[] | undefined = tree;
        const path = [...finalPath];
        while (path.length > 0 && level) {
            const cat = path.pop();
            const branch = level.find(node => isCategoryNode(node) && node.id === 'cat-' + cat?.id) as
                | ProductCategoryNode
                | undefined;
            if (branch?.children) {
                branch.showChildren = true;
                level = branch.children;
            } else {
                break;
            }
        }
    }

    return tree;
}
